<script setup lang="ts">
import { onMounted } from 'vue'

import type { CapabilityProfile } from '../types/callables'
import { useCapabilityProfiles } from '../composables/useCapabilityProfiles'
import { openDialog } from '../ui/dialogs'
import { toast } from '../ui/toasts'

import ConfirmDeleteProfileDialog, { type ConfirmDeleteResult } from './ConfirmDeleteProfileDialog.vue'
import EditProfileDialog, { type EditProfilePayload } from './EditProfileDialog.vue'

// Emitted whenever the server modified users/{uid} rows underneath us — a
// force-delete's batch-detach is the only case today. The parent (AdminView)
// owns the users list and refetches; we don't reach across composables here.
const emit = defineEmits<{
  usersChanged: [uids: string[]]
}>()

const { profiles, assignmentCounts, loading, error, load, save, remove } = useCapabilityProfiles()

onMounted(() => { void load() })

async function openCreate() {
  const created = await openDialog<EditProfilePayload>(EditProfileDialog, {
    save: async (payload: EditProfilePayload) => (await save(payload)) !== null,
  })
  if (created) toast.success('Profile created.')
}

async function openEdit(profile: CapabilityProfile) {
  const updated = await openDialog<EditProfilePayload>(EditProfileDialog, {
    profile,
    save: async (payload: EditProfilePayload) => (await save(payload)) !== null,
  })
  if (updated) toast.success('Profile updated.')
}

async function requestDelete(profile: CapabilityProfile) {
  const result = await openDialog<ConfirmDeleteResult>(ConfirmDeleteProfileDialog, {
    profile,
    initialAssigned: assignmentCounts.value[profile.id] ?? 0,
    remove: (force: boolean) => remove(profile.id, force),
  }, { size: 'narrow' })
  if (!result) return

  const detached = result.detachedFrom.length
  toast.success(
    detached > 0
      ? `Profile deleted and detached from ${detached} user${detached === 1 ? '' : 's'}.`
      : 'Profile deleted.',
  )
  // Server modified those users' `profiles` arrays under us; the parent's
  // users list is now stale and would render dangling pills. Tell it.
  if (detached > 0) emit('usersChanged', result.detachedFrom)
}
</script>

<template>
  <section class="panel">
    <header class="bar">
      <h2>Capability profiles</h2>
      <div class="spacer" />
      <button type="button" class="btn btn-sm" :disabled="loading" @click="load()">Refresh</button>
      <button type="button" class="btn btn-primary btn-sm" @click="openCreate">New profile</button>
    </header>

    <p class="hint">
      Profiles carry a <code>capabilities</code> claim embedded into a user's
      licence at issuance. Assign profiles to users below. A user with two or
      more profiles picks one when they open the app.
    </p>

    <p v-if="error" class="msg msg--error" role="alert">{{ error }}</p>

    <div class="wrap">
      <table>
        <thead>
          <tr>
            <th>Name</th>
            <th>Mode</th>
            <th>Capabilities</th>
            <th class="center">Assigned</th>
            <th class="actions-col">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="p in profiles" :key="p.id">
            <td>
              <div class="name">{{ p.name }}</div>
              <div v-if="p.description" class="muted">{{ p.description }}</div>
            </td>
            <td>{{ p.capabilities.mode === 'denyList' ? 'Deny-list' : 'Allow-list' }}</td>
            <td>
              <span v-if="!p.capabilities.list.length" class="muted">—</span>
              <code v-for="cap in p.capabilities.list" :key="cap" class="cap">{{ cap }}</code>
            </td>
            <td class="center">{{ assignmentCounts[p.id] ?? 0 }}</td>
            <td class="actions-col">
              <button type="button" class="btn btn-link btn-sm" :disabled="loading" @click="openEdit(p)">Edit</button>
              <button type="button" class="btn btn-link btn-danger btn-sm" :disabled="loading" @click="requestDelete(p)">Delete</button>
            </td>
          </tr>

          <tr v-if="!profiles.length && !loading">
            <td colspan="5" class="empty">
              No profiles yet. Users without an assigned profile receive unrestricted licences.
            </td>
          </tr>
        </tbody>
      </table>
    </div>

  </section>
</template>

<style scoped>
.panel { margin-bottom: var(--sp-6); }

.bar {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  margin-bottom: var(--sp-1);
}

h2 { margin: 0; font-size: 1.05rem; font-weight: 600; }

.hint code { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }

/* Table layout is genuinely unique to this panel — column widths, wrapping,
   and the pill code chips only make sense here. Kept scoped. */

.wrap { overflow-x: auto; }

table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

th, td {
  text-align: left;
  padding: var(--sp-2) var(--sp-3);
  border-bottom: 1px solid var(--border);
  vertical-align: top;
}

th {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--fg-muted);
  font-weight: 600;
  white-space: nowrap;
}

/* Assigned count and Actions cells are single-line content in rows that
   frequently wrap (Capabilities column stacks multiple code chips). Middle-
   align so the number and the Edit/Delete buttons line up with the row's
   perceived vertical centre rather than clinging to the top. */
td.center,
td.actions-col {
  vertical-align: middle;
}

/* Override global .muted to shrink the description font a touch inside table rows. */
.muted  { font-size: 0.8rem; }
.name   { font-weight: 500; }

code.cap {
  display: inline-block;
  padding: 0.05rem 0.35rem;
  margin: 0.1rem 0.25rem 0.1rem 0;
  border-radius: 3px;
  background: var(--bg-subtle);
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.78rem;
}

.actions-col { white-space: nowrap; text-align: right; }

.empty {
  text-align: center;
  color: var(--fg-muted);
  padding: var(--sp-5);
}
</style>
